import React, { useEffect, useState, useCallback } from 'react';
import { trainingAPI } from '../../services/trainingAPI';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const CAT_COLOR = {
  OPT:      'bg-blue-100 text-blue-700',
  STEM_OPT: 'bg-violet-100 text-violet-700',
  CPT:      'bg-green-100 text-green-700',
};

const TRAINING_VISA_TYPES = ['OPT', 'STEM_OPT', 'CPT'];
const TRAINING_TYPES = ['Client', 'Internal'];

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_OPTIONS = ['active', 'paused', 'completed'];

/* ══════════════════════════════════════════════════════════════════════════ */
export default function TrainingPage() {
  const [streams,     setStreams]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [openStream,  setOpenStream]  = useState(null);   // stream id expanded
  const [empList,     setEmpList]     = useState([]);
  const [empLoading,  setEmpLoading]  = useState(false);

  // stream form
  const [showForm,    setShowForm]    = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [formName,    setFormName]    = useState('');
  const [formDesc,    setFormDesc]    = useState('');
  const [saving,      setSaving]      = useState(false);
  const [formErr,     setFormErr]     = useState('');

  // delete confirm
  const [deleteId,    setDeleteId]    = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  // training type tab
  const [trainingType, setTrainingType] = useState('Client');

  // all employees table
  const [allEmps,         setAllEmps]         = useState([]);
  const [allEmpsLoading,  setAllEmpsLoading]  = useState(true);
  const [assigningId,     setAssigningId]     = useState(null);
  const [assignForm,      setAssignForm]      = useState({});
  const [assignSaving,    setAssignSaving]    = useState(false);
  const [assignErr,       setAssignErr]       = useState('');
  const [empSearch,       setEmpSearch]       = useState('');
  // stream form type field
  const [formType,        setFormType]        = useState('Client');

  const load = useCallback(() => {
    setLoading(true);
    trainingAPI.listStreams()
      .then(r => setStreams(r.data.data.streams || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadAllEmps = useCallback(() => {
    setAllEmpsLoading(true);
    trainingAPI.allEmployees()
      .then(r => setAllEmps(r.data.data.employees || []))
      .catch(() => {})
      .finally(() => setAllEmpsLoading(false));
  }, []);

  useEffect(() => { load(); loadAllEmps(); }, [load, loadAllEmps]);

  async function loadStreamEmployees(streamId) {
    if (openStream === streamId) { setOpenStream(null); return; }
    setOpenStream(streamId);
    setEmpLoading(true);
    try {
      const r = await trainingAPI.streamEmployees(streamId);
      setEmpList(r.data.data.employees || []);
    } catch {
      setEmpList([]);
    } finally {
      setEmpLoading(false);
    }
  }

  function openCreate() {
    setEditId(null); setFormName(''); setFormDesc(''); setFormType(trainingType);
    setFormErr(''); setShowForm(true);
  }

  function openEdit(s) {
    setEditId(s.id); setFormName(s.name); setFormDesc(s.description || ''); setFormType(s.training_type || 'Client');
    setFormErr(''); setShowForm(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!formName.trim()) { setFormErr('Stream name is required.'); return; }
    setSaving(true); setFormErr('');
    try {
      await trainingAPI.saveStream({ id: editId || undefined, name: formName.trim(), description: formDesc.trim(), training_type: formType });
      setShowForm(false);
      load();
    } catch (ex) {
      setFormErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await trainingAPI.deleteStream(deleteId);
      setDeleteId(null);
      if (openStream === deleteId) setOpenStream(null);
      load();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }

  function openAssign(emp) {
    setAssigningId(emp.id);
    setAssignErr('');
    setAssignForm({
      stream_id:       emp.stream_id   || '',
      start_date:      emp.training_start ? emp.training_start.split('T')[0].split(' ')[0] : '',
      status:          emp.training_status || 'active',
      supervisor_name: emp.supervisor_name || '',
    });
  }

  async function handleAssignSave(empId) {
    if (!assignForm.stream_id) { setAssignErr('Please select a stream.'); return; }
    if (!assignForm.start_date) { setAssignErr('Start date is required.'); return; }
    setAssignSaving(true); setAssignErr('');
    try {
      await trainingAPI.assign({
        employee_id:     Number(empId),
        stream_id:       Number(assignForm.stream_id),
        start_date:      assignForm.start_date,
        status:          assignForm.status,
        supervisor_name: assignForm.supervisor_name,
      });
      setAssigningId(null);
      load();        // refresh employee counts in streams
      loadAllEmps(); // refresh table
    } catch (ex) {
      setAssignErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setAssignSaving(false);
    }
  }

  const filteredEmps = allEmps.filter(e => {
    if (!TRAINING_VISA_TYPES.includes(e.category)) return false;
    if (!empSearch.trim()) return true;
    const q = empSearch.toLowerCase();
    return (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.stream_name || '').toLowerCase().includes(q)
    );
  });

  const filteredStreams = streams.filter(s =>
    !s.training_type || s.training_type === trainingType
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Training</h1>
          <p className="text-sm text-slate-500 mt-0.5">OPT / STEM OPT / CPT employee training streams</p>
        </div>
        <button onClick={openCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          <span className="text-base leading-none">+</span> New Stream
        </button>
      </div>

      {/* Client / Internal tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {TRAINING_TYPES.map(t => (
          <button key={t} onClick={() => setTrainingType(t)}
            className={`px-5 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              trainingType === t ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {t} Training
          </button>
        ))}
      </div>

      {/* Stream form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">{editId ? 'Edit Stream' : 'Create Stream'}</h3>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Stream Name <span className="text-red-500">*</span></label>
                <input value={formName} onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Java Backend, React Frontend"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Training Type <span className="text-red-500">*</span></label>
                <select value={formType} onChange={e => setFormType(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  {TRAINING_TYPES.map(t => <option key={t} value={t}>{t} Training</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <textarea value={formDesc} onChange={e => setFormDesc(e.target.value)}
                placeholder="Brief description of this training stream"
                rows={2}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            {formErr && <p className="text-red-600 text-sm">{formErr}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold">
                {saving ? 'Saving…' : editId ? 'Update Stream' : 'Create Stream'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Streams list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading streams…</div>
      ) : streams.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-400 text-sm">No training streams yet.</p>
          <button onClick={openCreate} className="mt-3 text-blue-600 text-sm hover:underline">Create your first stream</button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredStreams.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Stream header */}
              <div className="px-5 py-4 flex items-center gap-4">
                <button
                  onClick={() => loadStreamEmployees(s.id)}
                  className="flex-1 flex items-center gap-3 text-left hover:opacity-80 transition-opacity">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                    <span className="text-lg">🎓</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800">{s.name}</p>
                    {s.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{s.description}</p>}
                  </div>
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full shrink-0">
                    {s.employee_count} employee{s.employee_count !== 1 ? 's' : ''}
                  </span>
                  <span className="text-slate-400 text-xs shrink-0">{openStream === s.id ? '▲' : '▼'}</span>
                </button>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(s)}
                    className="text-xs border border-blue-200 text-blue-600 px-2.5 py-1 rounded-lg hover:bg-blue-50">Edit</button>
                  <button onClick={() => setDeleteId(s.id)}
                    className="text-xs border border-red-200 text-red-500 px-2.5 py-1 rounded-lg hover:bg-red-50">Delete</button>
                </div>
              </div>

              {/* Delete confirm */}
              {deleteId === s.id && (
                <div className="border-t border-red-100 bg-red-50 px-5 py-3 flex items-center gap-3">
                  <p className="text-sm text-red-700 flex-1">Delete stream <strong>{s.name}</strong>? All assignments will be removed.</p>
                  <button onClick={() => setDeleteId(null)} disabled={deleting}
                    className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-lg text-xs hover:bg-white">Cancel</button>
                  <button onClick={handleDelete} disabled={deleting}
                    className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-700">
                    {deleting ? '…' : 'Yes, Delete'}
                  </button>
                </div>
              )}

              {/* Employee list */}
              {openStream === s.id && (
                <div className="border-t border-slate-100">
                  {empLoading ? (
                    <div className="px-5 py-6 text-center text-slate-400 text-sm">Loading…</div>
                  ) : empList.length === 0 ? (
                    <div className="px-5 py-6 text-center text-slate-400 text-sm">No employees assigned to this stream.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                          <th className="px-5 py-2.5 text-left font-medium">Employee</th>
                          <th className="px-5 py-2.5 text-left font-medium">Category</th>
                          <th className="px-5 py-2.5 text-left font-medium">Start Date</th>
                          <th className="px-5 py-2.5 text-left font-medium">Status</th>
                          <th className="px-5 py-2.5 text-left font-medium">Supervisor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {empList.map(emp => (
                          <tr key={emp.employee_id} className="hover:bg-slate-50">
                            <td className="px-5 py-3">
                              <p className="font-medium text-slate-800">{emp.first_name} {emp.last_name}</p>
                              <p className="text-xs text-slate-400">{emp.email}</p>
                            </td>
                            <td className="px-5 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLOR[emp.category] || 'bg-slate-100 text-slate-600'}`}>
                                {emp.category?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-slate-600">{fmtDate(emp.start_date)}</td>
                            <td className="px-5 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                emp.status === 'active'    ? 'bg-green-100 text-green-700' :
                                emp.status === 'completed' ? 'bg-blue-100 text-blue-700'  :
                                                             'bg-yellow-100 text-yellow-700'}`}>
                                {emp.status}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-slate-600 text-xs">{emp.supervisor_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── All Employees ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-800">All Employees</h2>
            <p className="text-xs text-slate-500 mt-0.5">Assign or change stream for any active employee</p>
          </div>
          <input
            value={empSearch} onChange={e => setEmpSearch(e.target.value)}
            placeholder="Search by name, email or stream…"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        {allEmpsLoading ? (
          <div className="py-10 text-center text-slate-400 text-sm">Loading employees…</div>
        ) : filteredEmps.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">No employees found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="px-5 py-2.5 text-left font-medium">Employee</th>
                <th className="px-5 py-2.5 text-left font-medium">Category</th>
                <th className="px-5 py-2.5 text-left font-medium">Stream</th>
                <th className="px-5 py-2.5 text-left font-medium">Start Date</th>
                <th className="px-5 py-2.5 text-left font-medium">Status</th>
                <th className="px-5 py-2.5 text-left font-medium">Supervisor</th>
                <th className="px-5 py-2.5 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredEmps.map(emp => (
                <React.Fragment key={emp.id}>
                  <tr className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-800">{emp.first_name} {emp.last_name}</p>
                      <p className="text-xs text-slate-400">{emp.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLOR[emp.category] || 'bg-slate-100 text-slate-600'}`}>
                        {emp.category?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {emp.stream_name
                        ? <span className="font-medium text-blue-700">{emp.stream_name}</span>
                        : <span className="text-slate-400 italic">Unassigned</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{fmtDate(emp.training_start)}</td>
                    <td className="px-5 py-3">
                      {emp.training_status ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                          emp.training_status === 'active'    ? 'bg-green-100 text-green-700' :
                          emp.training_status === 'completed' ? 'bg-blue-100 text-blue-700'  :
                                                                'bg-yellow-100 text-yellow-700'}`}>
                          {emp.training_status.replace('_', ' ')}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-600 text-xs">{emp.supervisor_name || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => assigningId === emp.id ? setAssigningId(null) : openAssign(emp)}
                        className="text-xs border border-blue-200 text-blue-600 px-2.5 py-1 rounded-lg hover:bg-blue-50">
                        {emp.stream_id ? 'Change Stream' : 'Assign Stream'}
                      </button>
                    </td>
                  </tr>

                  {/* Inline assign form */}
                  {assigningId === emp.id && (
                    <tr>
                      <td colSpan={7} className="bg-blue-50 border-b border-blue-100 px-5 py-4">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 max-w-3xl">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Stream <span className="text-red-500">*</span></label>
                            <select
                              value={assignForm.stream_id}
                              onChange={e => setAssignForm(f => ({ ...f, stream_id: e.target.value }))}
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                              <option value="">— Select —</option>
                              {streams.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Start Date <span className="text-red-500">*</span></label>
                            <input type="date"
                              value={assignForm.start_date}
                              onChange={e => setAssignForm(f => ({ ...f, start_date: e.target.value }))}
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                            <select
                              value={assignForm.status}
                              onChange={e => setAssignForm(f => ({ ...f, status: e.target.value }))}
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Supervisor Name</label>
                            <input
                              value={assignForm.supervisor_name}
                              onChange={e => setAssignForm(f => ({ ...f, supervisor_name: e.target.value }))}
                              placeholder="Supervisor"
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        {assignErr && <p className="text-red-600 text-xs mt-2">{assignErr}</p>}
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setAssigningId(null)}
                            className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-lg text-xs hover:bg-white">Cancel</button>
                          <button onClick={() => handleAssignSave(emp.id)} disabled={assignSaving}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-1.5 rounded-lg text-xs font-semibold">
                            {assignSaving ? 'Saving…' : 'Save Assignment'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
